/**
 * Inspection model (UI name) ?? shared definitions for admin UI.
 *
 * Code/API still use "job" paths (`/jobs`, job_id). User-facing name = Inspection.
 *
 * An Inspection is one SS work order on one building for the current BWOF cycle.
 * Default track = SS inspection visit. After FAIL → repair track (same record, status REPAIRING).
 */

/** User-facing labels (nav, headings, buttons). */
export const INSPECTION_LABELS = {
  singular: 'Inspection',
  plural: 'Inspections',
  newItem: 'New Inspection',
  detail: 'Inspection',
  listOverdue: 'Inspections',
  backToList: 'Back to Inspections',
  create: 'Create inspection',
  open: 'Open',
  assigned: 'Assigned Inspections',
  orderCta: 'Issue manually',
  goToList: 'Go to Inspections',
};

/** Manual issue only ?? auto path is due → date → work pack. */
export const ISSUE_LABELS = {
  verb: 'Issue manually',
  pageTitle: 'Issue inspection',
  one: 'Issue inspection',
  next: 'Issue next inspection',
  allMissing: (n) => `Issue all missing (${n})`,
  hint:
    'Use only when due did not auto-create a work pack. Normal path: due asks the default IQP for a date, then the pack is issued.',
};

export function buildingInspectionsPath(buildingId) {
  return `/jobs/building/${buildingId}`;
}

export function issueManuallyPath(buildingId) {
  return `/buildings/${buildingId}/order`;
}

export const JOB_DEFINITION = {
  short:
    'An Inspection is one Specified System (SS) work order on one building ?? the scheduled check against the compliance schedule; after Fail it becomes the repair track for that SS.',
  long: `Inspection = building + SS code + due date + status.
• Inspection visit: check the SS (Form 12 / checklist).
• Repair track: same record after FAIL ?? quote, repair, re-inspect until Pass/Complete.
An Inspection is not “the whole building BWOF”; the building cycle closes when all SS inspections are done and the council pack is approved.`,
};

/** Who drives each status (admin web first; worker app later for field). */
export const JOB_STATUS_ACTORS = {
  PENDING: {
    actor: 'System or Admin',
    meaning: 'Work pack issued (auto after a visit date, or Issue manually). Date may still be open.',
    adminNext: 'Wait for the IQP date if Scheduling, or Schedule / Ask if still on us.',
  },
  QUERIED: {
    actor: 'Admin → Worker / company',
    meaning: 'Awaiting reply ?? offer sent by app or email; counterpart has not answered.',
    adminNext: 'Nudge, or wait. Do not Issue again unless the pack is missing.',
  },
  AUCTION: {
    actor: 'Admin → Worker',
    meaning: 'Open claim ?? first qualified worker can take the inspection.',
    adminNext: 'Monitor claims, or assign / complete as admin if needed.',
  },
  WAITING: {
    actor: 'Worker / Admin',
    meaning: 'Worker asked to wait (or soft hold).',
    adminNext: 'Follow up or reassign / schedule.',
  },
  ACCEPTED: {
    actor: 'Worker (accept) / Admin (assign)',
    meaning: 'A worker is responsible; date not fixed yet.',
    adminNext: 'Schedule the visit (Booking).',
  },
  SCHEDULED: {
    actor: 'Admin',
    meaning: 'Visit date/time set on calendar.',
    adminNext: 'Start work when on site (or after offline visit), then Complete or Fail.',
  },
  IN_PROGRESS: {
    actor: 'Worker app (later) / Admin (now)',
    meaning: 'Inspection or repair work is underway.',
    adminNext: 'Complete (pass) or Mark fail from Inspection detail after offline sheets.',
  },
  FAILED: {
    actor: 'Inspector outcome',
    meaning: 'Inspection failed ?? defects; repair path starts.',
    adminNext: 'Send reports, request quote, move to Repairing.',
  },
  REPAIRING: {
    actor: 'Admin + Worker/IQP',
    meaning: 'Defect repair / re-inspection in progress.',
    adminNext: 'Track Repairing page; Complete when re-inspect passes.',
  },
  NOTIFIED: {
    actor: 'System → IQP / company',
    meaning: 'Auto-created at lead time; prepare email sent; awaiting proposed date.',
    adminNext: 'Wait for email confirm, or Set date (manual) on detail.',
  },
  AFTER_PAPERWORK: {
    actor: 'IQP / company → Admin',
    meaning: 'Visit done; paperwork upload requested or received.',
    adminNext: 'Review uploads, then Confirm paperwork done → Completed.',
  },
  COMPLETED: {
    actor: 'Worker app or Admin',
    meaning: 'This SS inspection finished for this cycle.',
    adminNext: 'No inspection action ?? check building Close (council pack) when all SS done.',
  },
  REJECTED: {
    actor: 'Worker',
    meaning: 'Worker declined the offer.',
    adminNext: 'Query again or assign another worker.',
  },
};

/** List-page Info copy for filtered views (dashboard cards / status filter). */
export const LIST_FILTER_INFO = {
  DUE_WORK: {
    title: 'Due work (start here)',
    pageTitle: 'Inspections',
    body: `Same list as Dashboard → Due queue (Over Due + Urgent).
• Clock: Over Due / Due soon. The system asks default IQP / company for a visit date (Scheduling).
• When a date arrives, the work pack is issued automatically. Issue manually only if that pack is missing.
• Rows may have no pack yet; open the building Inspections list, not Issue, unless you must create one by hand.`,
  },
  OVERDUE: {
    title: 'Over Due',
    pageTitle: 'Inspections',
    body: `Past the due date ?? work is late (not the same as Urgent).
• Urgent = still due within 30 days; Over Due = already past.
• Admin: open each row, assign/schedule if needed, then Start → Complete or Fail on Inspection detail.
• Completing clears Over Due (badge and Over Due filter). Completed rows stay out of Open / Over Due; use filter **Completed (archive)** only when you need history.`,
  },
  URGENT: {
    title: 'Urgent',
    pageTitle: 'Inspections',
    body: `Still due within 30 days ?? not overdue yet.
• Separate from Over Due: past-due items appear only under Over Due.
• Wait for the IQP date (Scheduling), or Schedule if you must set it here.
• Same admin field lifecycle as other inspections once work is done.`,
  },
  PENDING: {
    title: 'Issued',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.PENDING.meaning}
• Who: ${JOB_STATUS_ACTORS.PENDING.actor}
• Admin next: ${JOB_STATUS_ACTORS.PENDING.adminNext}`,
  },
  'QUERIED,AUCTION': {
    title: 'Auction / Query',
    pageTitle: 'Inspections',
    body: `Queried: ${JOB_STATUS_ACTORS.QUERIED.meaning}
Auction: ${JOB_STATUS_ACTORS.AUCTION.meaning}
• Admin next: wait for accept, assign manually, or open Inspection and Start / Complete on behalf.`,
  },
  QUERIED: {
    title: 'Awaiting reply',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.QUERIED.meaning}
• Who: ${JOB_STATUS_ACTORS.QUERIED.actor}
• Admin next: ${JOB_STATUS_ACTORS.QUERIED.adminNext}`,
  },
  AUCTION: {
    title: 'Auction',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.AUCTION.meaning}
• Who: ${JOB_STATUS_ACTORS.AUCTION.actor}
• Admin next: ${JOB_STATUS_ACTORS.AUCTION.adminNext}`,
  },
  ACCEPTED: {
    title: 'Accepted',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.ACCEPTED.meaning}
• Who: ${JOB_STATUS_ACTORS.ACCEPTED.actor}
• Admin next: ${JOB_STATUS_ACTORS.ACCEPTED.adminNext}`,
  },
  SCHEDULED: {
    title: 'Scheduled',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.SCHEDULED.meaning}
• Who: ${JOB_STATUS_ACTORS.SCHEDULED.actor}
• Admin next: ${JOB_STATUS_ACTORS.SCHEDULED.adminNext}`,
  },
  NOTIFIED: {
    title: 'Notified',
    pageTitle: 'Inspections',
    body: `Lead-time reached: system created the inspection and emailed internal IQP + related company together.
• Listed facts: who was emailed (IQP / company), send count, last sent time, propose-date status.
• Appears on Schedule as a Notify chip on the compliance due date.
• Admin next: wait for email confirm, or set the visit date manually on the inspection detail.
• No reply → daily chase emails until a date is proposed (or you stop/override).`,
  },
  IN_PROGRESS: {
    title: 'In Progress',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.IN_PROGRESS.meaning}
• Who: ${JOB_STATUS_ACTORS.IN_PROGRESS.actor}
• Admin next: ${JOB_STATUS_ACTORS.IN_PROGRESS.adminNext}`,
  },
  AFTER_PAPERWORK: {
    title: 'After paperwork',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.AFTER_PAPERWORK.meaning}
• Who: ${JOB_STATUS_ACTORS.AFTER_PAPERWORK.actor}
• Admin next: ${JOB_STATUS_ACTORS.AFTER_PAPERWORK.adminNext}`,
  },
  REPAIRING: {
    title: 'Repairing',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.REPAIRING.meaning}
• Who: ${JOB_STATUS_ACTORS.REPAIRING.actor}
• Admin next: ${JOB_STATUS_ACTORS.REPAIRING.adminNext}`,
  },
  COMPLETED: {
    title: 'Completed (archive)',
    pageTitle: 'Inspections',
    body: `${JOB_STATUS_ACTORS.COMPLETED.meaning}
• Who: ${JOB_STATUS_ACTORS.COMPLETED.actor}
• Admin next: ${JOB_STATUS_ACTORS.COMPLETED.adminNext}`,
  },
};

const ADMIN_FLOW_BLURB =
  'Daily agent flow: notify assignee (lead time by frequency) → confirm date → Scheduled → day-of In progress → result arrives → Completed. Admin can always advance, edit, or delete on Inspection screens. Pending / Auction / Accept stay as legacy.';

/** Statuses kept in DB/API but de-emphasised in daily admin UI. */
export const LEGACY_ASSIGNMENT_STATUSES = ['PENDING', 'QUERIED', 'AUCTION', 'ACCEPTED', 'REJECTED'];

/**
 * Target ops story (email/app notify later; admin can force each step now).
 * Lead times: monthly 1 week · quarterly 3 weeks · semi/annual 1 month before due.
 */
export const INSPECTION_OPS_STORY = {
  short:
    'Before due (by frequency): notify default inspector or partner company → they confirm a date → Scheduled. On the day → In progress. When results arrive → Completed (or Fail → Repairing).',
  leadTimes: {
    MONTHLY: '2 weeks before due (visit remind 1 week)',
    QUARTERLY: '1 month before due (visit remind 2 weeks)',
    SEMI_ANNUAL: '1 month before due (visit remind 2 weeks)',
    ANNUAL: '2 months before due (visit remind 1 month)',
  },
  steps: [
    'Notify assignee (email now / app later) with proposed window',
    'Confirm date (button reply) → Scheduled; if no reply → contact + admin sets date',
    'Day of visit → In progress',
    'Results received → Completed (pass) or Failed → Repairing',
  ],
};

/**
 * Primary “next step” for admin advance button.
 * @returns {{ status: string, label: string, confirm: string } | null}
 */
export function getNextStageAction(status) {
  const map = {
    PENDING: {
      status: 'SCHEDULED',
      label: 'Confirm → Scheduled',
      confirm: 'Mark as Scheduled? (Set/confirm visit date if needed.)',
    },
    QUERIED: {
      status: 'SCHEDULED',
      label: 'Confirm → Scheduled',
      confirm: 'Skip query wait and mark Scheduled?',
    },
    AUCTION: {
      status: 'SCHEDULED',
      label: 'Confirm → Scheduled',
      confirm: 'Skip auction and mark Scheduled?',
    },
    ACCEPTED: {
      status: 'SCHEDULED',
      label: 'Confirm → Scheduled',
      confirm: 'Confirm visit date and mark Scheduled?',
    },
    WAITING: {
      status: 'SCHEDULED',
      label: 'Confirm → Scheduled',
      confirm: 'Mark Scheduled after follow-up?',
    },
    SCHEDULED: {
      status: 'IN_PROGRESS',
      label: 'Start work',
      confirm: 'Mark In Progress (visit underway / admin recording field work)?',
    },
    IN_PROGRESS: {
      status: 'COMPLETED',
      label: 'Complete (pass)',
      confirm: 'Mark Completed (pass)? Use when inspection results are in.',
    },
    FAILED: {
      status: 'REPAIRING',
      label: 'Start repairing',
      confirm: 'Move to Repairing?',
    },
    REPAIRING: {
      status: 'COMPLETED',
      label: 'Complete (pass)',
      confirm: 'Mark Completed after repair / re-inspect pass?',
    },
  };
  return map[status] || null;
}

/**
 * Resolve PageHeading title + Info for the inspections list.
 * @returns {{ title: string, infoTitle: string, infoBody: string }}
 */
export function getInspectionsListHeading({ overdue, urgent, statusFilter }) {
  const storyBlurb = `${INSPECTION_OPS_STORY.short}\n\n${ADMIN_FLOW_BLURB}`;
  if (overdue || statusFilter === 'OVERDUE') {
    const m = LIST_FILTER_INFO.OVERDUE;
    return { title: m.pageTitle, infoTitle: m.title, infoBody: `${m.body}\n\n${storyBlurb}` };
  }
  if (urgent) {
    const m = LIST_FILTER_INFO.URGENT;
    return { title: m.pageTitle, infoTitle: m.title, infoBody: `${m.body}\n\n${storyBlurb}` };
  }
  const key = statusFilter && statusFilter !== 'ALL' ? statusFilter : null;
  if (key && LIST_FILTER_INFO[key]) {
    const m = LIST_FILTER_INFO[key];
    return { title: m.pageTitle, infoTitle: m.title, infoBody: `${m.body}\n\n${storyBlurb}` };
  }
  if (key && JOB_STATUS_ACTORS[key]) {
    const a = JOB_STATUS_ACTORS[key];
    return {
      title: 'Inspections',
      infoTitle: key,
      infoBody: `${a.meaning}\n• Who: ${a.actor}\n• Admin next: ${a.adminNext}\n\n${storyBlurb}`,
    };
  }
  return {
    title: INSPECTION_LABELS.plural,
    infoTitle: 'What is an Inspection?',
    infoBody: `${JOB_DEFINITION.short}\n\n${JOB_DEFINITION.long}\n\n${storyBlurb}`,
  };
}

export function getAdminNextAction(status) {
  return JOB_STATUS_ACTORS[status]?.adminNext || 'Open inspection detail.';
}

export function getStatusActor(status) {
  return JOB_STATUS_ACTORS[status]?.actor || '—';
}

/**
 * Admin “force move” targets (calendar event / detail / list).
 * Booked / In Progress / Completed → real status.
 * Urgent / Over Due → due-date buckets (may adjust due_date so the item lands in that queue).
 */
export const FORCE_MOVE_OPTIONS = [
  {
    value: 'BOOKED',
    label: 'Booked (Scheduled)',
    destination: 'Scheduled',
    navigate: '/jobs?status=SCHEDULED',
  },
  {
    value: 'IN_PROGRESS',
    label: 'In Progress',
    destination: 'In Progress',
    navigate: '/jobs?status=IN_PROGRESS',
  },
  {
    value: 'URGENT',
    label: 'Urgent (≤30 days)',
    destination: 'Urgent',
    navigate: '/jobs?urgent=1',
  },
  {
    value: 'OVERDUE',
    label: 'Over Due',
    destination: 'Over Due',
    navigate: '/jobs?overdue=1',
  },
  {
    value: 'COMPLETED',
    label: 'Completed',
    destination: 'Completed',
    navigate: '/jobs?status=COMPLETED',
  },
];

/** Per-SS row dropdown: bulk-style actions + force-move (same menu on every SS line). */
export const SS_ROW_ACTIONS = [
  {
    value: 'email',
    label: 'Email overdue/urgent digest',
    destination: 'Mail',
    group: 'notify',
  },
  {
    value: 'schedule',
    label: 'Add to Schedule',
    destination: 'Schedule',
    group: 'schedule',
    needsJob: true,
  },
  ...FORCE_MOVE_OPTIONS.map((o) => ({
    value: `force_${o.value}`,
    label: o.label,
    destination: o.destination,
    group: 'move',
    needsJob: true,
  })),
];

/** Option text shown in the per-SS dropdown ?? destination only. */
export function formatSsRowActionLabel(action) {
  if (!action) return '';
  return action.destination || action.label || '';
}

function localYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Build PATCH body + confirm copy for a force-move choice.
 * @param {string} value - FORCE_MOVE_OPTIONS value
 * @param {{ due_date?: string, scheduled_date?: string, daysUntil?: number|null }} job
 */
export function buildForceMovePlan(value, job = {}) {
  const opt = FORCE_MOVE_OPTIONS.find((o) => o.value === value);
  if (!opt) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const days = job.daysUntil;

  const patch = { admin_note: `Force move → ${opt.label}` };
      let confirm = `Update this SS to “${opt.label}”? Stay on this page; the row updates from the database.`;

  if (value === 'BOOKED') {
    patch.status = 'SCHEDULED';
    if (!job.scheduled_date) patch.scheduled_date = localYmd(today);
    confirm = 'Mark this SS as Booked (Scheduled)? It will leave this list when no longer due-queue work.';
  } else if (value === 'IN_PROGRESS') {
    patch.status = 'IN_PROGRESS';
    confirm = 'Mark this SS as In Progress?';
  } else if (value === 'COMPLETED') {
    patch.status = 'COMPLETED';
    confirm = 'Mark this SS Completed? It will drop off this Due queue (still findable under Completed).';
  } else if (value === 'URGENT') {
    // Urgent = still due within 0–30 days (never overdue). Adjust due if needed.
    if (days == null || days < 0 || days > 30) {
      patch.due_date = localYmd(today);
      confirm = 'Set due date to today so this SS sits in the Urgent bucket on this page?';
    } else {
      confirm = 'Already in the Urgent window ?? keep on this page?';
    }
  } else if (value === 'OVERDUE') {
    if (days == null || days >= 0) {
      patch.due_date = localYmd(yesterday);
      confirm = 'Set due date to yesterday so this SS sits in Over Due on this page?';
    } else {
      confirm = 'Already overdue ?? keep on this page?';
    }
  }

  const needsPatch = Object.keys(patch).some((k) => k !== 'admin_note');
  return {
    value,
    label: opt.label,
    navigate: opt.navigate,
    patch: needsPatch ? patch : null,
    confirm,
  };
}
